import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { Button } from '@/components/ui/button';
import { Check, ArrowLeft, Sun, Moon, Layers, HelpCircle } from 'lucide-react';

const PricingPage = () => {
  const { isAuthenticated } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const plans = [
    {
      name: "Starter",
      price: 15,
      credits: 100,
      features: [
        "100 credits/month",
        "Basic AI tools",
        "Standard resolution outputs",
        "Email support",
        "7-day history"
      ],
      popular: false
    },
    {
      name: "Growth",
      price: 29,
      credits: 500,
      features: [
        "500 credits/month",
        "All AI tools",
        "HD outputs",
        "Priority processing",
        "Full workflow access",
        "30-day history",
        "Priority support"
      ],
      popular: true
    },
    {
      name: "Pro",
      price: 49,
      credits: 2000,
      features: [
        "2000 credits/month",
        "Unlimited tools",
        "4K outputs",
        "Fastest processing",
        "Bulk generation",
        "Premium mockups",
        "Unlimited history",
        "Dedicated support"
      ],
      popular: false
    }
  ];

  const handleSelect = (plan) => {
    if (isAuthenticated) {
      navigate('/dashboard/settings', { state: { selectedPlan: plan.name.toLowerCase() } });
    } else {
      navigate('/auth?mode=register');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="pricing-logo">
            <div className="w-8 h-8 bg-foreground rounded-lg flex items-center justify-center">
              <Layers className="w-5 h-5 text-background" />
            </div>
            <span className="font-semibold text-xl tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              MockupPro
            </span>
          </Link>
          
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={toggleTheme}>
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            {isAuthenticated ? (
              <Link to="/dashboard">
                <Button>Go to Dashboard</Button>
              </Link>
            ) : (
              <Link to="/auth">
                <Button>Sign In</Button>
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* Back Link */}
      <div className="max-w-7xl mx-auto px-6 pt-8">
        <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back to home</span>
        </Link>
      </div>

      {/* Pricing Content */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h1 
              className="text-4xl sm:text-5xl font-bold mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              data-testid="pricing-heading"
            >
              Simple, transparent pricing
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Start with a free trial. Upgrade anytime. Cancel anytime.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ 
                  y: plan.popular ? -6 : -4, 
                  transition: { duration: 0.15, ease: "easeOut" } 
                }}
                className={`relative bg-card border-2 rounded-2xl p-8 transition-shadow duration-150 cursor-pointer ${
                  plan.popular 
                    ? 'border-primary bg-primary/5 ring-1 ring-primary/20 shadow-lg shadow-primary/10 hover:shadow-xl hover:shadow-primary/15' 
                    : 'border-border hover:border-primary/30 hover:shadow-lg'
                }`}
                data-testid={`pricing-plan-${plan.name.toLowerCase()}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-primary text-primary-foreground text-xs font-medium px-3 py-1 rounded-full shadow-md">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <h3 
                  className="text-xl font-semibold mb-2"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {plan.name}
                </h3>
                
                <div className="mb-4">
                  <span className="text-4xl font-bold">${plan.price}</span>
                  <span className="text-muted-foreground">/month</span>
                </div>
                
                <p className="text-sm text-muted-foreground mb-6 pb-6 border-b border-border">
                  {plan.credits} credits included
                </p>
                
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 mt-0.5 text-primary flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                
                <Button 
                  className={`w-full rounded-lg transition-all duration-200 ${
                    plan.popular 
                      ? 'bg-primary hover:bg-primary/90 text-primary-foreground shadow-md hover:shadow-lg hover:shadow-primary/20' 
                      : ''
                  }`}
                  variant={plan.popular ? "default" : "outline"}
                  onClick={() => handleSelect(plan)}
                  data-testid={`select-${plan.name.toLowerCase()}-btn`}
                >
                  {isAuthenticated ? 'Select Plan' : 'Start Free Trial'}
                </Button>
              </motion.div>
            ))}
          </div>

          {/* FAQ or Additional Info */}
          <div className="mt-20 max-w-3xl mx-auto text-center">
            <h2 
              className="text-2xl font-bold mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Questions?
            </h2>
            <p className="text-muted-foreground mb-6">
              Every plan includes a 7-day free trial. No credit card required to start.
              You can upgrade, downgrade, or cancel anytime from your dashboard.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <div className="bg-muted/30 rounded-xl p-6">
                <h3 className="font-semibold mb-2">What are credits?</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Credits are used for each AI action. Different tools use different amounts.
                </p>
                <Link 
                  to="/how-credits-work" 
                  className="text-sm font-medium flex items-center gap-1.5 hover:underline"
                  style={{ color: '#4F7CFF' }}
                >
                  <HelpCircle className="w-4 h-4" />
                  Learn how credits work
                </Link>
              </div>
              <div className="bg-muted/30 rounded-xl p-6">
                <h3 className="font-semibold mb-2">Can I cancel anytime?</h3>
                <p className="text-sm text-muted-foreground">
                  Yes! Cancel instantly from your settings page. No questions asked,
                  no hidden fees.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PricingPage;
